import { z } from 'zod';
import { FirestoreDocId } from '../eos/scorecard.js';
/**
 * Issues board (`/issues`) — EOS Identify · Discuss · Solve workflow.
 * One Firestore doc per issue; server-mediated via
 * `POST/GET/PATCH/DELETE /api/orgs/:o/issues` + `POST .../issues/:id/vote`.
 *
 * Each issue carries a per-org monotonic short id (`ISS-12`) for human
 * reference at the L10, a vote count + voter set for ranking the
 * Identified pile, and optional `sources[]` provenance chips that link
 * back to the signal / insight / rock / briefing / note that surfaced
 * the issue in the first place — the IDS board is alive precisely
 * because it cites where each issue came from.
 *
 * Multi-tenant: every doc carries `orgId`; reads gate via
 * `isOrgMember(orgId)`. Writes are owner-or-admin (the original
 * identifier or the assigned owner can patch); votes are any org member.
 * Delete is admin-only — the L10 record stays visible.
 *
 * Schema invariants:
 *   - `ownerLabel` is denormalized at write time so the card renders
 *     the owner through `<EntityName>` without a per-row `users/:uid`
 *     lookup. The router re-snaps on owner reassignment.
 *   - `voterUids[]` is capped at 200 to keep the doc light — well past
 *     the largest L10 attendance count. Beyond that, the vote route
 *     returns 400.
 *   - `shortId` is assigned server-side by scanning the highest existing
 *     `ISS-N` for the org and incrementing.
 */
export const ISSUES_COLLECTION = 'issues';
/**
 * Per-org monotonic watermark for `shortId` allocation. One doc per
 * org at `issueWatermarks/{orgId}` holding `{ nextSeq, orgId, updatedAt }`.
 * The create path reads + increments this in the same Firestore
 * transaction that writes the new issue, so two concurrent creates can't
 * collide on the same `ISS-N`. Server-only writes (Admin SDK); clients
 * never read or write this collection directly.
 */
export const ISSUE_WATERMARKS_COLLECTION = 'issueWatermarks';
/** Cap on `voterUids[]` length to keep docs light + avoid runaway
 *  size on a viral issue. The vote route returns 400 when a new
 *  voter would push past this cap. Larger than any plausible L10. */
export const ISSUE_VOTER_CAP = 200;
/** Hard cap on `sources[]` array length — mirrors the Zod schema and
 *  is referenced by the append-merge path in the store. */
export const ISSUE_SOURCES_CAP = 20;
/** Per-append cap on `sourcesAppend` so a single PATCH can't blow
 *  the doc out. Server merges + clamps to {@link ISSUE_SOURCES_CAP}. */
export const ISSUE_SOURCES_APPEND_CAP = 10;
/** Format prefix for the user-facing short id (`ISS-12`). The store
 *  scans `^ISS-(\d+)$` for the org and increments — keeping the prefix
 *  in one place avoids the regex drift between schema + store. */
export const ISSUE_SHORT_ID_PREFIX = 'ISS';
/**
 * EOS IDS workflow: `Identified` (the pile) → `Discussing` (this L10)
 * → `Solved` (action assigned). Wickman-EOS spelling — preserved so
 * the existing facilitator-script copy matches verbatim.
 */
export const IssueStatus = z.enum(['Identified', 'Discussing', 'Solved']);
/**
 * Closed string set of provenance kinds an issue can cite. `signal` /
 * `insight` / `rock` / `briefing` / `note` carry typed refs into the
 * corresponding collections; `manual` is the human-entered fallback
 * (no upstream ref).
 */
export const IssueSourceKind = z.enum(['signal', 'insight', 'rock', 'briefing', 'note', 'manual']);
/**
 * Provenance chip on an issue card. `manual` kind has no upstream ref
 * (the human typed the issue directly); every other kind carries the
 * doc id of the cited signal / insight / rock / briefing / note as a
 * required, non-empty string. Discriminated union so the TS narrowing
 * works on `source.kind === 'manual'` without optional-ref ambiguity.
 */
export const IssueSource = z.discriminatedUnion('kind', [
    z.object({
        kind: z.enum(['signal', 'insight', 'rock', 'briefing', 'note']),
        ref: z.string().min(1),
        label: z.string().min(1).max(120),
    }),
    z.object({
        kind: z.literal('manual'),
        ref: z.string().optional(),
        label: z.string().min(1).max(120),
    }),
]);
// FirestoreDocId is imported from eos/scorecard.ts (canonical definition).
// Re-exported below so issues consumers can `import { FirestoreDocId } from
// '@leanopx/shared/issues'`. Used here on `ownerUid` + `createdByUid` so a
// malformed uid can't reach `users.doc(uid)` and throw.
/**
 * Trim-then-validate string — used for free-text titles + bodies so
 * `'   '.trim() === ''` fails validation instead of persisting as a
 * blank-looking row. The server trims at write time anyway; doing it
 * here pushes the failure to the validator (clear 400) rather than the
 * store (silent empty doc).
 */
function trimmedString(min, max, label) {
    return z
        .string()
        .transform((s) => s.trim())
        .refine((s) => s.length >= min, { message: `${label} is required` })
        .refine((s) => s.length <= max, { message: `${label} too long` });
}
/**
 * Optional trim-then-validate string — `undefined` passes through, a
 * trimmed empty string collapses to `undefined` (treat blank as absent),
 * and a non-blank trimmed value gets length-checked.
 */
function optionalTrimmedString(max, label) {
    return z
        .string()
        .optional()
        .transform((s) => {
        if (s === undefined)
            return undefined;
        const trimmed = s.trim();
        return trimmed.length === 0 ? undefined : trimmed;
    })
        .refine((s) => s === undefined || s.length <= max, { message: `${label} too long` });
}
export const Issue = z.object({
    id: z.string(),
    orgId: z.string(),
    /** User-facing monotonic id, e.g. `ISS-12`. Stamped at create time
     *  by scanning the highest existing for the org + incrementing. */
    shortId: z.string(),
    title: z.string().min(1).max(240),
    body: z.string().max(2000).optional(),
    status: IssueStatus,
    /** Firestore doc id of the owning user — gated by {@link FirestoreDocId}
     *  so a malformed value can't poison `users.doc(ownerUid)`. */
    ownerUid: FirestoreDocId,
    /** Denormalized at write time. Re-snapped on owner reassignment. */
    ownerLabel: z.string(),
    votes: z.number().int().min(0),
    /** Up to {@link ISSUE_VOTER_CAP} voters — kept on-doc so the toggle
     *  is a single transactional read+write. */
    voterUids: z.array(z.string()).default([]),
    sources: z.array(IssueSource).max(ISSUE_SOURCES_CAP).default([]),
    /** Firestore doc id of the creating user — same gate as `ownerUid`. */
    createdByUid: FirestoreDocId,
    createdAt: z.string(),
    updatedAt: z.string(),
    /** Stamped when the issue first transitions into `Solved`. Survives
     *  subsequent re-opens (status flips back) so the L10 record holds. */
    solvedAt: z.string().optional(),
});
/**
 * Create-request body. Server stamps `id`, `shortId`, `createdByUid`,
 * `ownerUid` (defaults to actor uid when omitted), `ownerLabel`,
 * `votes` (0), `voterUids` ([]), `createdAt`, `updatedAt`. `status`
 * defaults to `Identified` so the new issue lands on the left of the
 * IDS board.
 */
export const CreateIssueRequest = z.object({
    title: trimmedString(1, 240, 'title'),
    body: optionalTrimmedString(2000, 'body'),
    ownerUid: FirestoreDocId.optional(),
    status: IssueStatus.optional(),
    sources: z.array(IssueSource).max(ISSUE_SOURCES_CAP).optional(),
});
/**
 * Patch body. `body: null` clears the field; `ownerUid` triggers a
 * re-snap of `ownerLabel` server-side. `sourcesAppend` merges into the
 * existing `sources[]` array and the result is clamped to
 * {@link ISSUE_SOURCES_CAP}.
 *
 * Immutable fields (id / orgId / shortId / createdByUid / createdAt /
 * votes / voterUids) are deliberately absent — votes flip through the
 * dedicated `POST .../vote` route so the audit trail stays clean.
 */
export const UpdateIssueRequest = z.object({
    status: IssueStatus.optional(),
    title: trimmedString(1, 240, 'title').optional(),
    // `body: null` clears the field (router maps to `FieldValue.delete()`);
    // a non-blank trimmed string sets it; `body: '   '` collapses to a
    // no-op (treated as if `body` was not in the patch at all).
    body: z
        .union([
        z.null(),
        z
            .string()
            .transform((s) => {
            const trimmed = s.trim();
            // Empty-after-trim → undefined so the patch acts as a no-op.
            // (To clear the field, the client must send explicit `null`.)
            return trimmed.length === 0 ? undefined : trimmed;
        })
            .refine((s) => s === undefined || s.length <= 2000, { message: 'body too long' }),
    ])
        .optional(),
    ownerUid: FirestoreDocId.optional(),
    sourcesAppend: z.array(IssueSource).max(ISSUE_SOURCES_APPEND_CAP).optional(),
});
/** List response — newest issues first, ordered server-side. */
export const ListIssuesResponse = z.object({
    issues: z.array(Issue),
});
/** Vote response — returns the updated issue + the actor's new vote
 *  state (so the UI can flip the toggle without re-querying). */
export const VoteIssueResponse = z.object({
    issue: Issue,
    voted: z.boolean(),
});
